import { UITheme } from './UITheme';
import { bindUIFocus } from './UIButton';
import * as Phaser from 'phaser';
import { LocalizationManager } from '../managers/LocalizationManager';
import { createLogger } from '../utils/Logger';

const ORIENTATION_OVERLAY_DEBUG_BUILD = 'orientation-overlay-debug-2026-05-27.1';

export interface OrientationSnapshot {
  readonly touchCapable: boolean;
  readonly isPortrait: boolean;
  readonly overlayVisible: boolean;
  readonly lockAttempted: boolean;
  readonly lockSupported: boolean;
  readonly lockFailed: boolean;
  readonly fullscreenSupported: boolean;
  readonly fullscreenAttempted: boolean;
  readonly fullscreenActive: boolean;
  readonly fullscreenFailed: boolean;
}

export class OrientationOverlay {
  private readonly scene: Phaser.Scene;
  private readonly logger = createLogger('OrientationOverlay');
  private readonly background: Phaser.GameObjects.Rectangle;
  private readonly title: Phaser.GameObjects.Text;
  private readonly body: Phaser.GameObjects.Text;
  private readonly fullscreenButton: Phaser.GameObjects.Rectangle;
  private readonly fullscreenLabel: Phaser.GameObjects.Text;
  private releaseFocus?: () => void;
  private visible = true;
  private lockAttempted = false;
  private lockSupported = false;
  private lockFailed = false;
  private fullscreenAttempted = false;
  private fullscreenFailed = false;
  private lastSnapshotKey = '';
  private lastDecisionDebugKey = '';
  private readonly debugTransitionsEnabled = isOrientationDebugLoggingEnabled();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const loc = LocalizationManager.getInstance();
    const layout = this.resolveLayout();
    this.background = this.scene.add.rectangle(0, 0, scene.scale.width, scene.scale.height, UITheme.background, 0.94)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(6000).setInteractive();
    for(const event of ['pointerdown','pointerup']) this.background.on(event, (_p: unknown,_x: number,_y: number,e?: Phaser.Types.Input.EventData) => e?.stopPropagation());
    this.title = this.scene.add.text(layout.centerX, layout.titleY, loc.getText('mobile.orientation.title'), {
      fontFamily: UITheme.body, fontSize: '28px',
      color: '#ffffff',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: layout.wordWrapWidth }
    }).setOrigin(0.5).setScrollFactor(0).setDepth(6001);
    this.body = this.scene.add.text(layout.centerX, layout.bodyY, loc.getText('mobile.orientation.body'), {
      fontFamily: UITheme.body, fontSize: '18px',
      color: '#c8d2e0',
      align: 'center',
      wordWrap: { width: layout.wordWrapWidth }
    }).setOrigin(0.5).setScrollFactor(0).setDepth(6001);
    this.fullscreenButton = this.scene.add.rectangle(
      layout.centerX,
      layout.buttonY,
      layout.buttonWidth,
      layout.buttonHeight,
      UITheme.accent,
      0.92
    )
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(6002);
    this.setButtonInteractive(layout.buttonWidth, layout.buttonHeight);
    this.fullscreenButton.on('pointerup', this.handleFullscreenPointerUp, this);
    this.fullscreenLabel = this.scene.add.text(layout.centerX, layout.buttonY, loc.getText('mobile.fullscreen.cta'), {
      fontFamily: UITheme.body, fontSize: '18px',
      color: '#101f1c',
      fontStyle: 'bold',
      align: 'center',
      wordWrap: { width: layout.buttonWidth - 24 }
    }).setOrigin(0.5).setScrollFactor(0).setDepth(6003);
    this.updateFullscreenText();
    this.setVisible(false, 'constructor');
    console.info('[OrientationOverlay] debug build loaded', {
      build: ORIENTATION_OVERLAY_DEBUG_BUILD,
      initialDecision: resolveOrientationDecision(this.scene, false)
    });
  }

  public attemptLandscapeLock(): void {
    this.lockAttempted = true;
    this.lockSupported = typeof this.scene.scale.lockOrientation === 'function';

    if (!this.lockSupported) {
      this.logger.warn('orientation-lock:unsupported');
      return;
    }

    try {
      const result = this.scene.scale.lockOrientation('landscape');
      this.logger.info('orientation-lock:attempted', { result });
    } catch (error) {
      this.lockFailed = true;
      this.logger.warn('orientation-lock:failed', {
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  public update(touchCapable: boolean): OrientationSnapshot {
    const decision = resolveOrientationDecision(this.scene, touchCapable);
    (globalThis as any).__ORIENTATION_OVERLAY_DEBUG__ = {
      build: ORIENTATION_OVERLAY_DEBUG_BUILD,
      decision
    };
    if (this.debugTransitionsEnabled) {
      this.logger.debug('update:state', decision);
    }
    this.logVisibleDecision(decision);
    this.setVisible(decision.shouldShow, 'update');
    return this.getSnapshot(touchCapable);
  }

  public resize(): void {
    const layout = this.resolveLayout();
    this.background.setSize(this.scene.scale.width, this.scene.scale.height);
    this.title.setPosition(layout.centerX, layout.titleY);
    this.body.setPosition(layout.centerX, layout.bodyY);
    this.fullscreenButton.setPosition(layout.centerX, layout.buttonY);
    this.fullscreenButton.setSize(layout.buttonWidth, layout.buttonHeight);
    this.setButtonInteractive(layout.buttonWidth, layout.buttonHeight);
    this.fullscreenLabel.setPosition(layout.centerX, layout.buttonY);
    this.title.setWordWrapWidth?.(layout.wordWrapWidth, true);
    this.body.setWordWrapWidth?.(layout.wordWrapWidth, true);
    this.fullscreenLabel.setWordWrapWidth?.(layout.buttonWidth - 24, true);
    this.fullscreenLabel.setFixedSize?.(layout.buttonWidth - 24, layout.buttonHeight);
    this.logger.debug('resize:positioned', {
      width: this.scene.scale.width,
      height: this.scene.scale.height,
      wordWrapWidth: layout.wordWrapWidth,
      buttonWidth: layout.buttonWidth,
      buttonHeight: layout.buttonHeight
    });
  }

  public getSnapshot(touchCapable: boolean): OrientationSnapshot {
    const snapshot = {
      touchCapable,
      isPortrait: resolvePortraitState(this.scene),
      overlayVisible: this.visible,
      lockAttempted: this.lockAttempted,
      lockSupported: this.lockSupported,
      lockFailed: this.lockFailed,
      fullscreenSupported: this.resolveFullscreenSupported(),
      fullscreenAttempted: this.fullscreenAttempted,
      fullscreenActive: this.resolveFullscreenActive(),
      fullscreenFailed: this.fullscreenFailed
    };
    this.logSnapshotTransition(snapshot);
    return snapshot;
  }

  public handleFullscreenEntered(): void {
    this.fullscreenFailed = false;
    this.updateFullscreenText();
    this.logger.info('fullscreen:entered');
  }

  public handleFullscreenLeft(): void {
    this.updateFullscreenText();
    this.logger.info('fullscreen:left');
  }

  public handleFullscreenFailed(reason: unknown = 'unknown'): void {
    this.fullscreenFailed = true;
    this.updateFullscreenText();
    this.logger.warn('fullscreen:failed', { reason: normalizeFullscreenReason(reason) });
  }

  public handleFullscreenUnsupported(reason: unknown = 'unsupported'): void {
    this.fullscreenFailed = this.fullscreenAttempted;
    this.updateFullscreenText();
    this.logger.warn('fullscreen:unsupported', { reason: normalizeFullscreenReason(reason) });
  }

  public destroy(): void {
    this.releaseFocus?.();
    this.fullscreenButton.off?.('pointerup', this.handleFullscreenPointerUp, this);
    this.background.destroy();
    this.title.destroy();
    this.body.destroy();
    this.fullscreenButton.destroy();
    this.fullscreenLabel.destroy();
    this.logger.debug('destroy:complete');
  }

  private setVisible(isVisible: boolean, source: string): void {
    if (this.visible === isVisible) return;
    this.visible = isVisible;
    this.releaseFocus?.(); this.releaseFocus = undefined;
    if(isVisible) this.releaseFocus = bindUIFocus(this.scene,[{setFocused: focused => this.fullscreenButton.setStrokeStyle(focused ? 3 : 0,UITheme.warning),activate:()=>this.handleFullscreenPointerUp()}]);
    [this.background, this.title, this.body, this.fullscreenButton, this.fullscreenLabel].forEach(object => object.setVisible?.(isVisible));
    this.logger.debug('visibility:changed', { isVisible, source });
  }

  private handleFullscreenPointerUp(
    pointer?: Phaser.Input.Pointer,
    _localX?: number,
    _localY?: number,
    event?: { stopPropagation?: () => void }
  ): void {
    event?.stopPropagation?.();
    this.fullscreenAttempted = true;
    this.logger.debug('fullscreen:pointerup', {
      source: pointer?.wasTouch === false ? 'mouse' : 'touch',
      propagationStopped: typeof event?.stopPropagation === 'function',
      isPortrait: resolvePortraitState(this.scene),
      isFullscreen: this.resolveFullscreenActive(),
      fullscreenSupported: this.resolveFullscreenSupported(),
      overlayVisible: this.visible
    });

    if (this.resolveFullscreenActive()) {
      this.updateFullscreenText();
      return;
    }

    if (!this.resolveFullscreenSupported() || typeof this.scene.scale.startFullscreen !== 'function') {
      this.fullscreenFailed = true;
      this.updateFullscreenText();
      this.logger.warn('fullscreen:unsupported', { reason: 'scale-startFullscreen-unavailable' });
      return;
    }

    try {
      this.scene.scale.startFullscreen({ navigationUI: 'hide' });
      this.logger.info('fullscreen:requested', { navigationUI: 'hide' });
    } catch (error) {
      this.fullscreenFailed = true;
      this.updateFullscreenText();
      this.logger.warn('fullscreen:failed', { reason: normalizeFullscreenReason(error) });
    }
  }

  private updateFullscreenText(): void {
    const loc = LocalizationManager.getInstance();
    const key = this.resolveFullscreenActive()
      ? 'mobile.fullscreen.active'
      : this.resolveFullscreenSupported()
        ? 'mobile.fullscreen.cta'
        : 'mobile.fullscreen.unsupported';
    this.fullscreenLabel?.setText(loc.getText(key));
    this.fullscreenButton?.setFillStyle?.(this.resolveFullscreenSupported() ? UITheme.accent : 0x5f6877, 0.92);
  }

  private resolveFullscreenSupported(): boolean {
    const scale = this.scene.scale as Phaser.Scale.ScaleManager & {
      fullscreen?: { available?: boolean };
      startFullscreen?: unknown;
    };
    if (typeof scale.fullscreen?.available === 'boolean') {
      return scale.fullscreen.available === true;
    }

    return typeof scale.startFullscreen === 'function';
  }

  private resolveFullscreenActive(): boolean {
    return this.scene.scale?.isFullscreen === true;
  }

  private resolveLayout(): {
    centerX: number;
    titleY: number;
    bodyY: number;
    buttonY: number;
    wordWrapWidth: number;
    buttonWidth: number;
    buttonHeight: number;
  } {
    const width = this.scene.scale.width;
    const height = this.scene.scale.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const wordWrapWidth = Math.max(240, Math.min(width - 48, 520));
    const buttonWidth = Math.max(220, Math.min(width - 72, 360));
    const buttonHeight = 54;

    return {
      centerX,
      titleY: centerY - 88,
      bodyY: centerY - 24,
      buttonY: centerY + 72,
      wordWrapWidth,
      buttonWidth,
      buttonHeight
    };
  }

  private setButtonInteractive(width: number, height: number): void {
    const rectangle = new Phaser.Geom.Rectangle(0, 0, width, height);
    this.fullscreenButton.setInteractive({
      hitArea: rectangle,
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true
    });
  }

  private logSnapshotTransition(snapshot: OrientationSnapshot): void {
    if (!this.debugTransitionsEnabled) return;
    const key = JSON.stringify(snapshot);
    if (this.lastSnapshotKey === key) return;
    this.lastSnapshotKey = key;
    this.logger.debug('snapshot:changed', snapshot);
  }

  private logVisibleDecision(decision: OrientationDecision): void {
    if (!decision.shouldShow) return;
    const key = JSON.stringify(decision);
    if (this.lastDecisionDebugKey === key) return;
    this.lastDecisionDebugKey = key;
    this.logger.debug('showing-rotate-overlay', decision);
  }
}

export function resolvePortraitState(scene: Phaser.Scene): boolean {
  return resolveOrientationDecision(scene, true).isPortrait;
}

interface OrientationDecision {
  readonly touchCapable: boolean;
  readonly isPortrait: boolean;
  readonly isDesktopLike: boolean;
  readonly shouldShow: boolean;
  readonly viewport: { width: number; height: number } | null;
  readonly canvasBounds: { width: number; height: number } | null;
  readonly displaySize: { width: number; height: number } | null;
  readonly scaleSize: { width: number; height: number };
  readonly scaleIsPortrait: boolean | null;
  readonly userAgent: string;
  readonly platform: string;
  readonly maxTouchPoints: number;
  readonly media: {
    readonly pointerFine: boolean;
    readonly pointerCoarse: boolean;
    readonly hoverNone: boolean;
  };
}

export function resolveOrientationDecision(scene: Phaser.Scene, touchCapable: boolean): OrientationDecision {
  const viewport = resolveWindowViewport();
  const canvasBounds = resolveCanvasBounds(scene);
  const displaySize = resolveDisplaySize(scene);
  const preferredSize = viewport ?? canvasBounds ?? displaySize ?? {
    width: scene.scale.width,
    height: scene.scale.height
  };
  const media = {
    pointerFine: matchesMedia('(pointer: fine)'),
    pointerCoarse: matchesMedia('(pointer: coarse)'),
    hoverNone: matchesMedia('(hover: none)')
  };
  const nav = globalThis.navigator;
  const userAgent = nav?.userAgent ?? '';
  const platform = nav?.platform ?? '';
  const maxTouchPoints = Number(nav?.maxTouchPoints ?? 0);
  const isDesktopLike = resolveDesktopLikeBrowser({
    userAgent,
    platform,
    maxTouchPoints,
    media
  });
  const isPortrait = preferredSize.height > preferredSize.width;

  return {
    touchCapable,
    isPortrait,
    isDesktopLike,
    shouldShow: touchCapable && isPortrait && !isDesktopLike,
    viewport,
    canvasBounds,
    displaySize,
    scaleSize: {
      width: scene.scale.width,
      height: scene.scale.height
    },
    scaleIsPortrait: typeof scene.scale.isPortrait === 'boolean' ? scene.scale.isPortrait : null,
    userAgent,
    platform,
    maxTouchPoints,
    media
  };
}

function resolveWindowViewport(): { width: number; height: number } | null {
  const win = globalThis.window;
  if (isPositiveFiniteNumber(win?.innerWidth) && isPositiveFiniteNumber(win?.innerHeight)) {
    return { width: win.innerWidth, height: win.innerHeight };
  }

  return null;
}

function resolveCanvasBounds(scene: Phaser.Scene): { width: number; height: number } | null {
  const canvasBounds = scene.scale.canvas?.getBoundingClientRect?.();
  if (isPositiveFiniteNumber(canvasBounds?.width) && isPositiveFiniteNumber(canvasBounds?.height)) {
    return { width: canvasBounds.width, height: canvasBounds.height };
  }

  return null;
}

function resolveDisplaySize(scene: Phaser.Scene): { width: number; height: number } | null {
  const displaySize = scene.scale.displaySize;
  if (isPositiveFiniteNumber(displaySize?.width) && isPositiveFiniteNumber(displaySize?.height)) {
    return { width: displaySize.width, height: displaySize.height };
  }

  return null;
}

function isPositiveFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function resolveDesktopLikeBrowser(input: {
  readonly userAgent: string;
  readonly platform: string;
  readonly maxTouchPoints: number;
  readonly media: {
    readonly pointerFine: boolean;
    readonly pointerCoarse: boolean;
    readonly hoverNone: boolean;
  };
}): boolean {
  const userAgentData = globalThis.navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  if (userAgentData.userAgentData?.mobile === true) return false;
  if (/\b(Android|iPhone|iPad|iPod|Mobile|Windows Phone)\b/i.test(input.userAgent)) return false;

  const isIpadDesktopUserAgent = /\bMacintosh\b/i.test(input.userAgent)
    && input.maxTouchPoints > 1
    && input.media.pointerCoarse
    && input.media.hoverNone;
  if (isIpadDesktopUserAgent) return false;

  return /\b(Win32|Win64|Windows|MacIntel|Linux x86_64|X11)\b/i.test(input.platform)
    || /\b(Windows NT|X11; Linux x86_64|Macintosh)\b/i.test(input.userAgent);
}

function matchesMedia(query: string): boolean {
  const matchMedia = globalThis.matchMedia ?? globalThis.window?.matchMedia;
  return typeof matchMedia === 'function' && matchMedia(query).matches;
}

function normalizeFullscreenReason(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  return String(reason);
}

function isOrientationDebugLoggingEnabled(): boolean {
  return (globalThis as any).process?.env?.LOG_LEVEL === 'debug';
}
